import * as React from 'react'

import { action, makeObservable } from 'mobx'
import { inject, observer } from 'mobx-react'
import { RouteComponentProps, withRouter } from 'react-router'

import MobileCommonStore from '~/client/src/mobile/stores/ui/MobileCommon.store'
import Spinner from '~/client/src/shared/components/Spinner/Spinner'
import DeliveriesStore from '~/client/src/shared/stores/domain/Deliveries.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import MaterialCategoriesStore from '~/client/src/shared/stores/domain/MaterialCategories.store'
import MaterialsStore from '~/client/src/shared/stores/domain/Materials.store'
import { notifyOverlayOpen } from '~/client/src/shared/utils/overlayNotifier'

import MobileInitialState from '../../stores/MobileInitialState'
import DeliveryDetailsWrapper from './components/DeliveryDetailsWrapper'

interface IProps {
  common?: MobileCommonStore
  state?: MobileInitialState
  locationAttributesStore?: LocationAttributesStore
  deliveriesStore?: DeliveriesStore
  materialCategoryStore?: MaterialCategoriesStore
  materialsStore?: MaterialsStore
}

interface IRouteParams {
  deliveryId: string
}

@inject(
  'common',
  'state',
  'locationAttributesStore',
  'deliveriesStore',
  'materialCategoryStore',
  'materialsStore',
)
@observer
class DeliveryDetailsView extends React.Component<
  IProps & RouteComponentProps<IRouteParams>
> {
  private hasDeliveryBeenCreated: boolean = false
  private closeOverlay: (() => void) | null = null

  public constructor(props: IProps & RouteComponentProps<IRouteParams>) {
    super(props)
    makeObservable(this)
  }

  public componentDidMount() {
    this.closeOverlay = notifyOverlayOpen()
  }

  public componentDidUpdate(
    prevProps: IProps & RouteComponentProps<IRouteParams>,
  ) {
    // The component stays mounted when the route param changes (delivery
    // creation pushes a new deliveryId on the same route). Each navigation
    // resets the overlay counter via the navigation listener in
    // MobileCommonStore, so re-register here so the nav bar stays hidden
    // while we're still inside details.
    if (
      prevProps.match.params.deliveryId !== this.props.match.params.deliveryId
    ) {
      this.closeOverlay?.()
      this.closeOverlay = notifyOverlayOpen()
    }
  }

  public componentWillUnmount() {
    this.closeOverlay?.()
    this.closeOverlay = null
  }

  private get requestedDeliveryId(): string {
    return this.props.match.params.deliveryId
  }

  public render() {
    const { common, state } = this.props
    const { hideNavBar } = common

    if (this.isLoading) {
      return (
        <div className="full-height row x-center y-center">
          <Spinner size="lg" />
        </div>
      )
    }

    return (
      <DeliveryDetailsWrapper
        deliveryId={this.requestedDeliveryId}
        projectId={state.activeProject.id}
        onBackClick={this.backClicked}
        onCreationCallback={this.onCreationCallback}
        onMount={hideNavBar}
      />
    )
  }

  @action.bound
  private backClicked() {
    const { common } = this.props

    common.toggleNavBar()

    // We push the delivery details view second time when delivery is created
    // Hence need to take that step back as well
    if (this.hasDeliveryBeenCreated) {
      this.hasDeliveryBeenCreated = false
      return common.goBack(common.displayDeliveriesView, -2)
    }

    common.goBack(common.displayDeliveriesView)
  }

  private get isLoading(): boolean {
    return (
      !this.props.locationAttributesStore.isDataReceived ||
      !this.props.deliveriesStore.isDataReceived ||
      !this.props.materialCategoryStore.isDataReceived ||
      !this.props.materialsStore.isDataReceived
    )
  }

  private onCreationCallback = (id: string) => {
    this.hasDeliveryBeenCreated = true
    this.props.common.displayDeliveryDetailsView(id)
  }
}

export default withRouter(DeliveryDetailsView)
